// A visitor gets one identity, and keeps it.
//
// This used to be regenerated from scratch in eight different components, so a word
// saved while reading a lesson landed under one anonymous id and the notepad, having
// invented another, never showed it. Everything now asks this module.
//
// localStorage alone is not enough: clearing site data, or a browser that drops it,
// used to take a guest's saved words with it. The same id is mirrored into a
// long-lived cookie so their work survives far longer than a browsing session.

const STORAGE_KEY = "sessionId";
const COOKIE_NAME = "smap_session";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 400; // ~13 months: the practical cookie ceiling

const createId = () =>
  `anon_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

const readCookie = (name) => {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/^])/g, "\\$1") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name, value) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
};

/** The id for whoever is reading, created once and then reused. */
export const getSessionId = () => {
  if (typeof window === "undefined") return null;

  let id = null;
  try {
    id = window.localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    // private mode can refuse localStorage; the cookie below still covers us
  }
  if (!id) id = readCookie(COOKIE_NAME);
  if (!id) id = createId();

  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch (error) {
    /* nothing to do: the cookie is the fallback */
  }
  // refresh the cookie on every read so active visitors keep their work even longer
  if (readCookie(COOKIE_NAME) !== id) writeCookie(COOKIE_NAME, id);
  return id;
};

/**
 * Headers for any request that can be answered for a signed-out visitor.
 * The token wins when it exists; the session id is the fallback the API reads.
 */
export const identityHeaders = (extra = {}) => {
  const raw = (() => {
    try {
      return window.localStorage.getItem("jstoken") || "";
    } catch (error) {
      return "";
    }
  })();
  const token = raw
    .trim()
    .split(/\s+/)
    .filter((part) => part && part.toLowerCase() !== "bearer")
    .pop();

  const headers = { ...extra };
  if (token) headers.Authorization = `Bearer ${token}`;
  headers["x-session-id"] = getSessionId();
  return headers;
};

export default getSessionId;
